import { useTranslation } from 'module/hooks/use-translation';
import { Avatar } from 'module/ui/components/atoms/avatar';
import { HumanisedDate } from 'module/ui/components/atoms/humanised-date';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { MoreActionsDropdown } from 'module/ui/components/atoms/more-actions';
import { Switch } from 'module/ui/components/atoms/switch/switch';
import { DraggableTable } from 'module/ui/components/molecules/draggable-table';
import {
  TableContainer,
  TableHeader,
} from 'module/ui/components/molecules/draggable-table/styles';
import { ColumnType } from 'module/ui/components/molecules/draggable-table/types';
import { Short01 } from 'module/ui/components/styles';
import { faEdit, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { Typography } from 'antd';
import { Tooltip } from 'antd';
import { SignalConfiguration } from 'api/signal/types';
import moment from 'moment';
import { FC, useMemo, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FlexWrapperWithShortGap } from 'components/common/styles';
import { signalTypeConfigs } from 'components/organisms/signal/signal-type/type-configs';
import { useDeleteSignalConfirmModal } from 'components/organisms/signal/use-delete-signal-confirm-modal';
import { SignalPermissions } from 'components/pages/settings/signal-configuration/list/page';

const { Text } = Typography;
const SignalName = styled(Text)`
  overflow: hidden;
  text-wrap: nowrap;
  text-overflow: ellipsis;
  whitespace: nowrap;
  flex: 1;
  min-width: 0;
  ${Short01};
  cursor: pointer;
`;
const StyledDraggableTable = styled.div`
  ${TableContainer} {
    max-height: calc(100vh - 220px);
    overflow-y: auto;
  }

  ${TableHeader} {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: var(--white-100);
  }
`;
const Wrapper = styled.div`
  margin: 0 var(--spacing-06);
`;

const LastUpdatedBy = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100px;
`;

const LastUpdatedOn = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 75px;
`;

const SignalTypeIconSlot = styled.span`
  width: 16px;
  min-width: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ColumnHeaderWithInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
  min-width: 0;
`;

const ColumnHeaderTitle = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const InfoIcon = styled(FontAwesomeIcon)`
  color: var(--icon-secondary);
  cursor: default;
`;

type UserLike = {
  fullName?: string;
  firstName?: string;
  lastName?: string;
  company?: {
    companyLogoUrl?: string;
  };
};

const getUserDisplayName = (value: unknown): string | null => {
  if (value == null) {
    return null;
  }

  if (typeof value !== 'object') {
    return null;
  }

  const user = value as UserLike;
  const fullName = user.fullName?.trim();

  if (fullName) {
    return fullName;
  }

  const first = user.firstName?.trim() ?? '';
  const last = user.lastName?.trim() ?? '';
  const combined = `${first} ${last}`.trim();

  if (combined) {
    return combined;
  }

  return null;
};

const getUserCompanyLogoUrl = (value: unknown): string | null => {
  if (value == null || typeof value !== 'object') {
    return null;
  }
  const user = value as UserLike;
  const url = user.company?.companyLogoUrl?.trim();

  return url?.length ? url : null;
};

const getSignalAuditMeta = (record: SignalConfiguration) => {
  return {
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
    createdBy: (record as any).createdByUser ?? (record as any).createdBy,
    updatedBy: (record as any).updatedByUser ?? (record as any).updatedBy,
    isEnabled: record.isEnabled ?? record.enabled ?? false,
    visibleOnTimeline: record.visibleOnTimeline,
  };
};

interface Props {
  isModuleFed?: boolean;
  signals: SignalConfiguration[];
  signalPermissions?: SignalPermissions;
  onEdit: (signalConfiguration: SignalConfiguration) => void;
  onDelete: (signalConfiguration: SignalConfiguration) => void;
  onStatusChange?: (
    signalConfiguration: SignalConfiguration,
    nextVisibleOnTimeline: boolean,
  ) => void;
  onEnabledChange?: (
    signalConfiguration: SignalConfiguration,
    nextIsEnabled: boolean,
  ) => void;
}

const SignalActionsCell: FC<{
  signal: SignalConfiguration;
  onEdit: (signal: SignalConfiguration) => void;
  onDelete: (signal: SignalConfiguration) => void;
  isEnabled: boolean;
  onDisable?: (signal: SignalConfiguration) => void;
  isModuleFed?: boolean;
  signalPermissions?: SignalPermissions;
}> = ({
  signal,
  onEdit,
  onDelete,
  isEnabled,
  onDisable,
  isModuleFed,
  signalPermissions,
}) => {
  const confirmDelete = useDeleteSignalConfirmModal({
    title: useTranslation('settings.signal_configuration.delete.confirm.title'),
    content: useTranslation(
      'settings.signal_configuration.delete.confirm.message',
    ),
    onOk: () => {
      onDelete(signal);
    },
    isEnabled,
    onDisable: () => onDisable?.(signal),
    autoFocusButton: null,
  });

  return (
    <MoreActionsDropdown
      menu={{
        onClick: ({ key }) => {
          if (key === 'edit') {
            onEdit(signal);
          } else if (key === 'delete') {
            confirmDelete();
          }
        },
        items: [
          {
            type: 'group',
            label: <FormattedMessage id="more_actions" />,
            children: [
              {
                key: 'edit',
                label: <FormattedMessage id="edit" />,
                icon: <FontAwesomeIcon icon={faEdit} />,
                disabled:
                  isModuleFed &&
                  !!signalPermissions &&
                  signalPermissions?.canWrite === false,
              },
              {
                key: 'delete',
                label: <FormattedMessage id="delete" />,
                icon: <FontAwesomeIcon icon={faTrash} />,
                danger: true,
                disabled:
                  isModuleFed &&
                  !!signalPermissions &&
                  signalPermissions?.canDelete === false,
              },
            ],
          },
        ],
      }}
    />
  );
};

export const SignalList: FC<Props> = ({
  signalPermissions,
  isModuleFed,
  signals,
  onEdit,
  onDelete,
  onStatusChange,
  onEnabledChange,
}) => {
  const renderHeaderWithInfo = useCallback(
    (title: React.ReactNode, tooltipId: string) => (
      <ColumnHeaderWithInfo>
        <ColumnHeaderTitle>{title}</ColumnHeaderTitle>
        <Tooltip title={<FormattedMessage id={tooltipId} />}>
          <InfoIcon icon={faCircleInfo} size={12} />
        </Tooltip>
      </ColumnHeaderWithInfo>
    ),
    [],
  );

  const sortedSignals = useMemo(
    () => [...signals].sort((a, b) => a.signalName.localeCompare(b.signalName)),
    [signals],
  );

  const [localStatusById, setLocalStatusById] = useState<
    Record<string, boolean>
  >({});
  const [localEnabledById, setLocalEnabledById] = useState<
    Record<string, boolean>
  >({});

  const toggleLocalStatus = useCallback((signalId: string, next: boolean) => {
    setLocalStatusById((prev) => ({ ...prev, [signalId]: next }));
  }, []);

  const toggleLocalEnabled = useCallback((signalId: string, next: boolean) => {
    setLocalEnabledById((prev) => ({ ...prev, [signalId]: next }));
  }, []);

  const columns: ColumnType<SignalConfiguration & { id: string }>[] = [
    {
      key: 'signalName',
      title: <FormattedMessage id="signal.fields.signal_name" />,
      width: 'minmax(100px, 1fr)',
      render: (value, record) => (
        <SignalName onClick={() => onEdit(record)}>{value}</SignalName>
      ),
    },
    {
      key: 'signalType',
      title: <FormattedMessage id="signal.fields.signal_type" />,
      width: '160px',
      render: (_value, record) => {
        const config = record.signalType
          ? signalTypeConfigs[record.signalType]
          : null;

        return config ? (
          <FlexWrapperWithShortGap>
            <SignalTypeIconSlot>{config.icon}</SignalTypeIconSlot>
            <LastUpdatedBy>{config.name}</LastUpdatedBy>
          </FlexWrapperWithShortGap>
        ) : (
          '-'
        );
      },
    },
    {
      key: 'updatedBy',
      title: <FormattedMessage id="last_updated_by" />,
      width: '200px',
      render: (_userId, record) => {
        const { updatedBy } = getSignalAuditMeta(record);
        const name = getUserDisplayName(updatedBy);
        const logoUrl = getUserCompanyLogoUrl(updatedBy);

        return name ? (
          <FlexWrapperWithShortGap>
            <Avatar name={name} size={20} src={logoUrl ?? undefined} />
            <LastUpdatedBy>{name}</LastUpdatedBy>
          </FlexWrapperWithShortGap>
        ) : (
          '-'
        );
      },
    },
    {
      key: 'updatedAt',
      title: <FormattedMessage id="last_updated_on" />,
      width: '140px',
      render: (_date, record) => {
        const { updatedAt } = getSignalAuditMeta(record);

        return (
          <LastUpdatedOn>
            {updatedAt ? <HumanisedDate timestamp={moment(updatedAt)} /> : '-'}
          </LastUpdatedOn>
        );
      },
    },
    {
      key: 'enabled',
      title: renderHeaderWithInfo(
        <FormattedMessage id="settings.signal_configuration.list.enable" />,
        'settings.signal_configuration.list.enable.tooltip',
      ),
      render: (_value, record) => {
        const { isEnabled } = getSignalAuditMeta(record);
        const recordChecked = Boolean(isEnabled);
        const checked =
          localEnabledById[record.id] === undefined
            ? recordChecked
            : localEnabledById[record.id];

        return (
          <Switch
            checked={checked}
            onChange={() => {
              const next = !checked;

              toggleLocalEnabled(record.id, next);
              onEnabledChange?.(record, next);
            }}
            size="small"
          />
        );
      },
      width: '110px',
    },
    {
      key: 'showOnTimeline',
      title: renderHeaderWithInfo(
        <FormattedMessage id="settings.signal_configuration.list.show_on_timeline" />,
        'settings.signal_configuration.list.show_on_timeline.tooltip',
      ),
      render: (_value, record) => {
        const { visibleOnTimeline } = getSignalAuditMeta(record);
        const recordChecked = Boolean(visibleOnTimeline);
        const checked =
          localStatusById[record.id] === undefined
            ? recordChecked
            : localStatusById[record.id];

        return (
          <Switch
            checked={checked}
            onChange={() => {
              const next = !checked;

              toggleLocalStatus(record.id, next);
              onStatusChange?.(record, next);
            }}
            size="small"
          />
        );
      },
      width: '160px',
    },
    {
      key: 'actions',
      width: '56px',
      title: '',
      render: (_value, record) => {
        const { isEnabled } = getSignalAuditMeta(record);
        const recordChecked = Boolean(isEnabled);
        const effectiveEnabled =
          localEnabledById[record.id] === undefined
            ? recordChecked
            : localEnabledById[record.id];

        // Keep the optimistic UI behavior the same as the Enable column toggle.
        const disable = (signal: SignalConfiguration) => {
          toggleLocalEnabled(record.id, false);
          onEnabledChange?.(signal, false);
        };

        return (
          <SignalActionsCell
            isModuleFed={isModuleFed}
            signalPermissions={signalPermissions}
            signal={record}
            onEdit={onEdit}
            onDelete={onDelete}
            isEnabled={Boolean(effectiveEnabled)}
            onDisable={disable}
          />
        );
      },
    },
  ];

  return (
    <Wrapper>
      <StyledDraggableTable>
        <DraggableTable<SignalConfiguration & { id: string }>
          columns={columns}
          data={sortedSignals.map((signal) => ({
            ...signal,
            id: String(signal.signalConfigurationId),
          }))}
          disableDrag={() => true}
        />
      </StyledDraggableTable>
    </Wrapper>
  );
};
